import React, { useState, useEffect, useRef } from 'react';
import { Asset } from 'contentful';
import Lottie from 'lottie-react';
import axios from 'axios';
import logger from '@greenlightme/logger';
import classNames from 'classnames';

import { ResponsiveTokenSet } from '../../theme/tokens/types';
import { ITextOnlyHeroFields } from '../../../types/contentful';
import { getHeroThemeClasses } from './themeConfig';

import CtaWidget from '../CtaWidget';
import CapsuleBadge from '../AtomicComponents/CapsuleBadge';
import PictogramComponent from '../AtomicComponents/Pictogram';
import { Asset as MediaAsset } from '../AtomicComponents/Asset';
import { NewVideoPlayer } from '../NewVideoPlayer';

interface Props extends ITextOnlyHeroFields {
  yMarginTopOverride?: ResponsiveTokenSet<string>;
  yMarginBottomOverride?: ResponsiveTokenSet<string>;
}

function isLottieAsset(asset?: Asset): boolean {
  const contentType = asset?.fields?.file?.contentType ?? '';
  const fileName = asset?.fields?.file?.fileName ?? '';
  return (
    contentType.includes('json') ||
    contentType.includes('zip') ||
    fileName.endsWith('.lottie') ||
    fileName.endsWith('.json')
  );
}

function isVideoAsset(asset?: Asset): boolean {
  const contentType = asset?.fields?.file?.contentType ?? '';
  return contentType.includes('video');
}

export function TextOnlyHero({
  capsule,
  pictogram,
  headingText,
  heroCopy,
  buttonAnchor,
  buttonColor,
  buttonText,
  ctaWidget,
  removeBottomPadding,
  yMarginTopOverride,
  yMarginBottomOverride,
  colorTheme = 'none',
  invertColors = false,
  logo,
  mediaAsset,
  videoPlayer
}: Props) {
  const theme = getHeroThemeClasses(colorTheme, invertColors);
  const isNoBackground = colorTheme === 'none';

  const [lottieData, setLottieData] = useState(null);
  const lottieRef = useRef(null);

  useEffect(() => {
    if (mediaAsset && isLottieAsset(mediaAsset) && !lottieData) {
      const url = mediaAsset.fields.file.url;
      const fullUrl = url.startsWith('http') ? url : `https:${url}`;
      axios
        .get(fullUrl)
        .then(({ data }) => setLottieData(data))
        .catch((err) => {
          logger.error('Error fetching lottie data', err);
          console.error('Error fetching lottie data', err);
        });
    }
  }, [mediaAsset, lottieData]);

  return (
    <section
      className={classNames('block-container', theme.background, {
        [`mt-${yMarginTopOverride?.[0]}`]: yMarginTopOverride?.[0],
        [`mobile:mt-${yMarginTopOverride?.mobile}`]: yMarginTopOverride?.mobile,
        [`tablet:mt-${yMarginTopOverride?.tablet}`]: yMarginTopOverride?.tablet,
        [`desktop:mt-${yMarginTopOverride?.desktop}`]:
          yMarginTopOverride?.desktop,
        [`mb-${yMarginBottomOverride?.[0]}`]: yMarginBottomOverride?.[0],
        [`mobile:mb-${yMarginBottomOverride?.mobile}`]:
          yMarginBottomOverride?.mobile,
        [`tablet:mb-${yMarginBottomOverride?.tablet}`]:
          yMarginBottomOverride?.tablet,
        [`desktop:mb-${yMarginBottomOverride?.desktop}`]:
          yMarginBottomOverride?.desktop
      })}
      data-blockid="text-only-hero"
      data-testid="text-only-hero"
      data-e2e="hero"
    >
      <div
        className={classNames(
          'mx-auto flex max-w-max flex-col gap-6 px-6',
          'items-start desktop:items-center',
          isNoBackground ? 'py-8' : 'py-12',
          { '!pb-0': removeBottomPadding }
        )}
      >
        {/* Text content — centered on desktop, left-aligned on mobile/tablet */}
        <div
          className={classNames(
            'flex w-full flex-col gap-4 self-stretch',
            'items-start desktop:mx-auto desktop:items-center'
          )}
        >
          {logo && (
            <MediaAsset
              asset={logo}
              className="h-auto w-[100px] object-contain tablet:w-[120px] desktop:w-[140px]"
            />
          )}
          {pictogram && <PictogramComponent {...pictogram.fields} />}
          {capsule && <CapsuleBadge {...capsule.fields} />}
          <h1
            className={classNames(
              'typography-display-1 m-0 font-normal',
              'text-left desktop:w-8/12 desktop:text-center',
              theme.heading
            )}
          >
            {headingText}
          </h1>
          {heroCopy && (
            <h3
              className={classNames(
                'typography-display-4 font-normal',
                'text-left desktop:text-center',
                theme.text
              )}
            >
              {heroCopy}
            </h3>
          )}
        </div>

        {/* CTA area */}
        <div className="flex w-full flex-col items-start gap-4 desktop:items-center">
          {buttonText && buttonAnchor && (
            <a
              className={classNames(
                'typography-button-md focus-visible:ring-offset-current flex min-w-44 max-w-lg shrink-0 flex-col items-center justify-center rounded-button-shape p-4 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent4-base focus-visible:ring-offset-4',
                {
                  'bg-callout': buttonColor === 'registration',
                  'bg-primary text-on-primary-surface-dark':
                    buttonColor === 'primary'
                }
              )}
              type="button"
              href={buttonAnchor}
            >
              {buttonText}
            </a>
          )}
          {ctaWidget && (
            <CtaWidget
              {...ctaWidget.fields}
              className={classNames(
                'w-full',
                theme.text,
                'desktop:[&_.role-select-container]:items-center',
                'desktop:[&_.role-select-label]:!text-center',
                '[&_.role-select-label]:![color:inherit]',
                {
                  '[&_[data-testid=privacy-disclaimer]]:text-on-screen-surface-dark':
                    theme.isStrongest
                }
              )}
            />
          )}
        </div>

        {/* Media asset area */}
        {mediaAsset && (
          <div className="w-full">
            {isLottieAsset(mediaAsset) && lottieData ? (
              <Lottie
                lottieRef={lottieRef}
                animationData={lottieData}
                loop
                className="mx-auto h-auto w-full max-w-3xl"
                data-testid="hero-lottie"
              />
            ) : isVideoAsset(mediaAsset) ? (
              <div className="w-full overflow-hidden rounded-2xl">
                <MediaAsset
                  asset={mediaAsset}
                  className="h-auto w-full"
                  muted
                  loop
                  controls={false}
                />
              </div>
            ) : (
              <div className="w-full">
                <MediaAsset
                  asset={mediaAsset}
                  className="mx-auto h-auto w-full overflow-hidden rounded-card-shape"
                />
              </div>
            )}
          </div>
        )}

        {/* Full-width video player component */}
        {videoPlayer && (
          <div className="w-full">
            <NewVideoPlayer {...videoPlayer.fields} inHero />
          </div>
        )}
      </div>
    </section>
  );
}
